import {useContext, useEffect, useState} from 'react';
import {Box, Button, LinearProgress, Sheet, Stack, Typography} from '@mui/joy';
import {motion} from 'framer-motion';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import {LayoutContext} from '../../context';
import {APIGetQualityDashboardBrief} from '../../api';

const number = value => value == null || value === '' || typeof value === 'boolean' || !Number.isFinite(Number(value)) ? null : Number(value);
const percentage = value => number(value) === null ? '—' : `${Math.round(number(value))}%`;

function Metric({icon, label, value, color}) {
  return <Box sx={{minWidth: 0, p: 1.25, borderRadius: '12px', bgcolor: 'background.surface', border: '1px solid', borderColor: 'divider', '@container (max-width: 360px)': {display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}}>
    <Stack direction="row" spacing={0.6} alignItems="center" sx={{color, mb: 0.65}}>
      {icon}<Typography level="body-xs" sx={{color: 'text.secondary', lineHeight: 1.4, overflowWrap: 'anywhere'}}>{label}</Typography>
    </Stack>
    <Typography level="title-md" sx={{fontWeight: 700, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere'}}>{value}</Typography>
  </Box>;
}

export default function QualityDashboardBrief() {
  const {tokenEmployee, account} = useContext(LayoutContext);
  const eligible = Array.isArray(account?.position) && account.position.includes('quality_master');
  const scope = `${account?.id || ''}:${tokenEmployee || ''}`;
  const [loaded, setLoaded] = useState(null), [loading, setLoading] = useState(false), [error, setError] = useState(false), [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoaded(null); setError(false); setLoading(false);
    if (!tokenEmployee || !eligible) return;
    setLoading(true);
    APIGetQualityDashboardBrief(tokenEmployee).then(result => {
      if (cancelled) return;
      if (!result || result.error) {setError(true); return;}
      setLoaded({scope, result});
    }).catch(() => {if (!cancelled) setError(true);})
      .finally(() => {if (!cancelled) setLoading(false);});
    return () => {cancelled = true;};
  }, [tokenEmployee, account?.id, eligible, retry]);
  const data = loaded?.scope === scope ? loaded.result : null;
  if (!tokenEmployee || !eligible || (!data && !loading && !error)) return null;
  const progress = data?.progress || {}, percent = number(data?.overall_percent), bonus = number(data?.bonus_calculation?.total_salary);
  const cycle = number(progress.avg_cycle_time_days);
  const progressColor = percent >= 80 ? 'success' : percent >= 50 ? 'warning' : 'primary';
  return <motion.div initial={{opacity: 0, y: -6}} animate={{opacity: 1, y: 0}} transition={{duration: 0.25}}>
    <Sheet aria-label="Мои показатели качества" sx={{p: {xs: 1.5, sm: 2}, mb: 2, minWidth: 0, borderRadius: '16px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.level1', overflow: 'hidden', containerType: 'inline-size'}}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{minWidth: 0}}>
        <Box sx={{width: 36, height: 36, flex: '0 0 auto', display: 'grid', placeItems: 'center', borderRadius: '11px', bgcolor: 'primary.softBg', color: 'primary.600'}}><FlagRoundedIcon sx={{fontSize: 20}}/></Box>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Typography level="title-md" sx={{fontWeight: 700, lineHeight: 1.4}}>Мои показатели качества</Typography>
          <Typography level="body-xs" sx={{mt: 0.3, color: 'text.tertiary'}}>Текущий месяц</Typography>
        </Box>
      </Stack>
      {loading && <LinearProgress aria-label="Загрузка показателей качества" sx={{mt: 1.5}}/>}
      {error && <Stack role="alert" spacing={0.75} sx={{mt: 1.5}}>
        <Typography level="body-sm" sx={{color: 'text.secondary'}}>Не удалось загрузить показатели</Typography>
        <Button size="sm" variant="soft" onClick={() => setRetry(value => value + 1)} sx={{alignSelf: 'flex-start'}}>Повторить</Button>
      </Stack>}
      {data && <>
        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mt: 1.75, '@container (max-width: 360px)': {gridTemplateColumns: 'minmax(0, 1fr)'}}}>
          <Metric icon={<VerifiedRoundedIcon sx={{fontSize: 16}}/>} label="В срок" value={percentage(progress.deadline_compliance)} color="primary.600"/>
          <Metric icon={<SpeedRoundedIcon sx={{fontSize: 16}}/>} label="Конверсия" value={percentage(progress.funnel_conversion)} color="warning.600"/>
          <Metric icon={<TimerRoundedIcon sx={{fontSize: 16}}/>} label="Цикл" value={cycle === null ? '—' : `${cycle.toLocaleString('ru-RU', {minimumFractionDigits: 1, maximumFractionDigits: 1})} дн`} color="text.secondary"/>
        </Box>
        <Box sx={{mt: 1.5}}>
          {data.has_goal ? <>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
              <Typography level="body-xs" sx={{color: 'text.secondary'}}>Выполнение цели</Typography>
              <Typography level="body-sm" sx={{fontWeight: 700}}>{percentage(percent)}</Typography>
            </Stack>
            {percent !== null && <LinearProgress determinate value={Math.max(0, Math.min(percent, 100))} color={progressColor} sx={{mt: 0.75, '--LinearProgress-radius': '99px'}}/>}
          </> : <Typography level="body-xs" sx={{color: 'text.secondary', lineHeight: 1.5}}>Цель на этот месяц пока не поставлена</Typography>}
          {data.has_goal && bonus !== null && bonus > 0 && <Typography level="body-xs" sx={{mt: 1, color: 'success.700'}}>Расчётная выплата: ≈ {Math.round(bonus).toLocaleString('ru-RU')} ₽</Typography>}
        </Box>
      </>}
    </Sheet>
  </motion.div>;
}
